/* Zuuz theme scripts */

function formatMoney(cents) {
  const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
  const amount = cents / 100;
  return new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency',
    currency,
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2
  }).format(amount);
}

async function refreshCartCount() {
  try {
    const cart = await fetch(`${window.routes.root_url.replace(/\/$/, '')}/cart.js`).then((r) => r.json());
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count > 0 ? cart.item_count : '';
    });
  } catch (e) {
    /* count refreshes on next page load */
  }
}

/* Cart toast */
const cartToast = {
  el: null,
  timer: null,
  show(text) {
    this.el = this.el || document.getElementById('CartToast');
    if (!this.el) return;
    if (text) this.el.querySelector('[data-cart-toast-text]').textContent = text;
    this.el.hidden = false;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.hide(), 6000);
  },
  hide() {
    if (this.el) this.el.hidden = true;
  }
};
document.addEventListener('click', (event) => {
  if (event.target.closest('[data-cart-toast-close]')) cartToast.hide();
});

/* Menu drawer */
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-drawer-open]');
  if (opener) {
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    drawer.setAttribute('open', '');
    opener.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    drawer.querySelector('[data-drawer-close]:not(.drawer__backdrop)')?.focus();
    return;
  }
  const closer = event.target.closest('[data-drawer-close]');
  if (closer) {
    const drawer = closer.closest('.drawer');
    drawer.removeAttribute('open');
    document.body.style.overflow = '';
    document.querySelector(`[data-drawer-open="${drawer.id}"]`)?.setAttribute('aria-expanded', 'false');
  }
});
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('.drawer[open] .drawer__backdrop').forEach((el) => el.click());
});

/* Collection filters: submit on change */
document.addEventListener('change', (event) => {
  const form = event.target.closest('[data-facets-form]');
  if (form && !event.target.matches('[data-no-autosubmit]')) form.requestSubmit();
});

/* Product builder */
class ProductBuilder extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.form = this.querySelector('[data-product-form]');
    this.fieldsets = [...this.querySelectorAll('fieldset[data-option-index]')];
    this.variantInput = this.querySelector('[data-variant-input]');
    this.addButton = this.querySelector('[data-add-button]');
    this.errorEl = this.querySelector('[data-product-error]');
    this.scrollAddon = this.querySelector('[data-scroll-addon]');
    this.qtyInput = this.querySelector('[data-qty-input]');
    this.plaque = this.querySelector('[data-plaque-color]');
    this.sticky = document.querySelector('[data-sticky-buy]');
    this.title = this.querySelector('.product__title')?.textContent.trim() || '';

    this.addEventListener('change', (event) => {
      if (event.target.closest('fieldset[data-option-index]')) this.onOptionChange();
      if (event.target === this.scrollAddon) this.updateTotal();
    });
    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-thumb]');
      if (thumb) this.showMedia(thumb.dataset.thumb);
      const step = event.target.closest('[data-qty]');
      if (step) {
        const next = Math.max(1, (parseInt(this.qtyInput.value, 10) || 1) + parseInt(step.dataset.qty, 10));
        this.qtyInput.value = next;
        this.updateTotal();
      }
    });
    this.qtyInput?.addEventListener('input', () => this.updateTotal());
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));

    this.setupSticky();
    this.onOptionChange({ initial: true });
  }

  selectedOptions() {
    return this.fieldsets.map((fs) => fs.querySelector('input:checked')?.value);
  }

  currentVariant() {
    if (!this.fieldsets.length) return this.variants[0];
    const selected = this.selectedOptions();
    return this.variants.find((v) => v.options.every((value, i) => value === selected[i]));
  }

  onOptionChange({ initial = false } = {}) {
    const selected = this.selectedOptions();
    const variant = this.currentVariant();

    this.fieldsets.forEach((fs, index) => {
      const valueEl = fs.querySelector('[data-option-value]');
      if (valueEl) valueEl.textContent = selected[index] || '';
      fs.querySelectorAll('input').forEach((input) => {
        const combo = [...selected];
        combo[index] = input.value;
        const match = this.variants.find((v) => v.options.every((value, i) => value === combo[i]));
        input.classList.toggle('is-unavailable', !match || !match.available);
      });
      const hint = fs.querySelector('[data-size-hint]');
      if (hint) {
        const note = [...fs.querySelectorAll('template[data-size-note]')].find(
          (t) => t.dataset.sizeNote === selected[index]
        );
        hint.textContent = note ? note.innerHTML.trim() : '';
        hint.hidden = !note;
      }
    });

    if (this.plaque) {
      const wantsPlaque = selected.some((value) => value && value.toLowerCase().includes('plaque'));
      this.plaque.hidden = !wantsPlaque;
      this.plaque.querySelector('select').disabled = !wantsPlaque;
    }

    this.updateVariant(variant, selected, initial);
  }

  updateVariant(variant, selected, initial) {
    const strings = window.themeStrings;
    if (!variant) {
      this.addButton.disabled = true;
      this.addButton.textContent = strings.unavailable;
      return;
    }
    this.variant = variant;
    this.variantInput.value = variant.id;
    this.addButton.disabled = !variant.available;
    this.addButton.textContent = variant.available ? strings.addToCart : strings.soldOut;

    const priceEl = this.querySelector('[data-product-price] .price__current');
    if (priceEl) priceEl.textContent = formatMoney(variant.price);
    const stickyPrice = this.sticky?.querySelector('[data-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
    this.updateTotal();

    if (variant.featured_media) {
      this.showMedia(variant.featured_media.id);
    } else {
      // No image attached to this variant: fall back to an image whose alt text names a selected value (e.g. "Navy Blue").
      const slides = [...this.querySelectorAll('.gallery__slide[data-media-id]')];
      const byAlt = slides.find((slide) =>
        selected.some((value) => value && slide.dataset.alt && slide.dataset.alt.includes(value.toLowerCase()))
      );
      if (byAlt) this.showMedia(byAlt.dataset.mediaId);
    }

    if (!initial) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  updateTotal() {
    const totalEl = this.querySelector('[data-buy-total]');
    if (!totalEl || !this.variant) return;
    const qty = Math.max(1, parseInt(this.qtyInput.value, 10) || 1);
    const withScroll = this.scrollAddon && this.scrollAddon.checked;
    const total = (this.variant.price + (withScroll ? parseInt(this.scrollAddon.dataset.price, 10) : 0)) * qty;
    totalEl.textContent = withScroll ? `Total with scroll: ${formatMoney(total)}` : '';
    totalEl.hidden = !withScroll;
  }

  showMedia(mediaId) {
    const id = String(mediaId);
    this.querySelectorAll('.gallery__slide').forEach((slide) => {
      slide.classList.toggle('is-active', slide.dataset.mediaId === id);
    });
    this.querySelectorAll('[data-thumb]').forEach((thumb) => {
      thumb.setAttribute('aria-current', thumb.dataset.thumb === id ? 'true' : 'false');
    });
  }

  async onSubmit(event) {
    event.preventDefault();
    if (!this.variant || this.addButton.disabled) return;
    const strings = window.themeStrings;
    const quantity = Math.max(1, parseInt(this.qtyInput.value, 10) || 1);
    const properties = {};
    for (const [key, value] of new FormData(this.form)) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && String(value).trim() !== '') properties[match[1]] = value;
    }

    const items = [{ id: this.variant.id, quantity, properties }];
    if (this.scrollAddon && this.scrollAddon.checked) {
      items.push({
        id: parseInt(this.scrollAddon.value, 10),
        quantity,
        properties: { For: `${this.title} \u2013 ${this.variant.title}` }
      });
    }

    this.addButton.disabled = true;
    this.addButton.textContent = strings.adding;
    this.errorEl.hidden = true;
    try {
      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message);
      await refreshCartCount();
      cartToast.show(items.length > 1 ? `${this.title} and scroll added to your cart` : `${this.title} added to your cart`);
    } catch (error) {
      this.errorEl.textContent = error.message || 'Something went wrong. Please try again.';
      this.errorEl.hidden = false;
    } finally {
      this.addButton.disabled = !this.variant.available;
      this.addButton.textContent = this.variant.available ? strings.addToCart : strings.soldOut;
    }
  }

  setupSticky() {
    if (!this.sticky || !this.addButton || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.sticky.classList.toggle('is-visible', show);
      this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
    });
    observer.observe(this.addButton);
  }
}
customElements.define('product-builder', ProductBuilder);
